import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Collapse,
  Chip,
  useMediaQuery,
  useTheme,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Business from '@mui/icons-material/Business';
import Person from '@mui/icons-material/Person';
import Search from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import StatusPill from '../components/StatusPill';
import companiesApi, { Company, CompanyListItem, CompanyContact } from '../services/companyService';
import { formatApiError } from '../utils/formatApiError';

const PRICING_TIERS = [1, 2, 3, 4, 5] as const;

const mobileIconButtonSx = { minWidth: 48, minHeight: 48 };
const mobilePrimaryButtonSx = { minHeight: 48, py: 1.5 };

const emptyCompanyForm = () => ({
  name: '',
  account_number: '',
  pricing_tier: 1,
  address: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
  is_active: true,
});

const emptyContactForm = () => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  position: '',
  is_active: true,
});

const CompaniesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedCompany, setExpandedCompany] = useState<Company | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm());

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactCompanyId, setContactCompanyId] = useState<number | null>(null);
  const [editingContact, setEditingContact] = useState<CompanyContact | null>(null);
  const [contactForm, setContactForm] = useState(emptyContactForm());

  useEffect(() => {
    const tmr = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(tmr);
  }, [search]);

  const loadCompanies = useCallback(() => {
    setListLoading(true);
    companiesApi
      .list(debouncedSearch || undefined)
      .then(setCompanies)
      .catch(() => setCompanies([]))
      .finally(() => setListLoading(false));
  }, [debouncedSearch]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const loadCompanyDetail = useCallback(
    (id: number) => {
      setDetailLoading(true);
      companiesApi
        .get(id)
        .then((c) => {
          setExpandedCompany(c);
        })
        .catch(() => setExpandedCompany(null))
        .finally(() => setDetailLoading(false));
    },
    [],
  );

  useEffect(() => {
    if (expandedId == null) {
      setExpandedCompany(null);
      return;
    }
    loadCompanyDetail(expandedId);
  }, [expandedId, loadCompanyDetail]);

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const openCreateCompany = () => {
    setEditingCompanyId(null);
    setCompanyForm(emptyCompanyForm());
    setCompanyDialogOpen(true);
  };

  const openEditCompany = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      const c = await companiesApi.get(id);
      setEditingCompanyId(c.id);
      setCompanyForm({
        name: c.name,
        account_number: c.account_number ?? '',
        pricing_tier: c.pricing_tier,
        address: c.address ?? '',
        city: c.city ?? '',
        state: c.state ?? '',
        zip_code: c.zip_code ?? '',
        phone: c.phone ?? '',
        is_active: c.is_active,
      });
      setCompanyDialogOpen(true);
    } catch (err: any) {
      alert(formatApiError(err, t('common.save')));
    }
  };

  const handleSaveCompany = async () => {
    try {
      const payload: Partial<Company> = {
        name: companyForm.name,
        account_number: companyForm.account_number || null,
        pricing_tier: companyForm.pricing_tier,
        address: companyForm.address || null,
        city: companyForm.city || null,
        state: companyForm.state || null,
        zip_code: companyForm.zip_code || null,
        phone: companyForm.phone || null,
      };
      if (editingCompanyId != null) {
        payload.is_active = companyForm.is_active;
        await companiesApi.update(editingCompanyId, payload);
      } else {
        await companiesApi.create({ ...payload, is_active: true });
      }
      setCompanyDialogOpen(false);
      loadCompanies();
      if (expandedId != null) loadCompanyDetail(expandedId);
    } catch (e: any) {
      alert(formatApiError(e, t('common.save')));
    }
  };

  const openCreateContact = (companyId: number) => {
    setContactCompanyId(companyId);
    setEditingContact(null);
    setContactForm(emptyContactForm());
    setContactDialogOpen(true);
  };

  const openEditContact = (companyId: number, contact: CompanyContact) => {
    setContactCompanyId(companyId);
    setEditingContact(contact);
    setContactForm({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      position: contact.position ?? '',
      is_active: contact.is_active,
    });
    setContactDialogOpen(true);
  };

  const handleSaveContact = async () => {
    if (contactCompanyId == null) return;
    try {
      const payload: Partial<CompanyContact> = {
        first_name: contactForm.first_name,
        last_name: contactForm.last_name,
        email: contactForm.email || null,
        phone: contactForm.phone || null,
        position: contactForm.position || null,
      };
      if (editingContact) {
        payload.is_active = contactForm.is_active;
        await companiesApi.updateContact(contactCompanyId, editingContact.id, payload);
      } else {
        await companiesApi.createContact(contactCompanyId, { ...payload, is_active: true });
      }
      setContactDialogOpen(false);
      loadCompanies();
      if (expandedId === contactCompanyId) loadCompanyDetail(contactCompanyId);
    } catch (e: any) {
      alert(formatApiError(e, t('common.save')));
    }
  };

  const locationLabel = (c: CompanyListItem) =>
    [c.city, c.state].filter(Boolean).join(', ') || '—';

  const renderContactsSection = (companyId: number, contacts: CompanyContact[]) => (
    <Box sx={{ pt: 1, pb: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {t('companies.contacts', { defaultValue: 'Contacts' })}
        </Typography>
        <Button
          size={isMobile ? 'medium' : 'small'}
          variant="outlined"
          startIcon={<Person />}
          onClick={() => openCreateContact(companyId)}
          sx={isMobile ? mobilePrimaryButtonSx : undefined}
        >
          {t('companies.add_contact', { defaultValue: 'Add contact' })}
        </Button>
      </Box>
      {contacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('companies.no_contacts', { defaultValue: 'No contacts yet.' })}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {contacts.map((ct) => (
            <Paper key={ct.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {ct.first_name} {ct.last_name}
                  </Typography>
                  {ct.position && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {ct.position}
                    </Typography>
                  )}
                  {ct.email && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {ct.email}
                    </Typography>
                  )}
                  {ct.phone && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {ct.phone}
                    </Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <StatusPill
                      value={ct.is_active}
                      trueLabel={t('companies.status_active', { defaultValue: 'Active' })}
                      falseLabel={t('companies.status_inactive', { defaultValue: 'Inactive' })}
                    />
                  </Box>
                </Box>
                <IconButton
                  aria-label={t('companies.edit_contact', { defaultValue: 'Edit contact' })}
                  onClick={() => openEditContact(companyId, ct)}
                  size={isMobile ? 'medium' : 'small'}
                  sx={isMobile ? mobileIconButtonSx : undefined}
                >
                  <Edit fontSize={isMobile ? 'medium' : 'small'} />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );

  const expandedContent = (companyId: number) => {
    if (expandedId !== companyId) return null;
    if (detailLoading || !expandedCompany || expandedCompany.id !== companyId) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography color="text.secondary">{t('common.loading')}</Typography>
        </Box>
      );
    }
    return renderContactsSection(companyId, expandedCompany.contacts || []);
  };

  return (
    <Box sx={{ px: isMobile ? 1 : 0 }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: isMobile ? '1.5rem' : undefined }}>
        {t('companies.title', { defaultValue: 'Companies' })}
      </Typography>

      <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
        <TextField
          fullWidth
          size={isMobile ? 'medium' : 'small'}
          placeholder={t('companies.search', { defaultValue: 'Search companies…' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateCompany}
            fullWidth={isMobile}
            sx={isMobile ? mobilePrimaryButtonSx : undefined}
          >
            {t('companies.new', { defaultValue: 'New Company' })}
          </Button>
        </Box>

        {listLoading ? (
          <Typography color="text.secondary">{t('common.loading')}</Typography>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {companies.map((c) => {
              const open = expandedId === c.id;
              return (
                <Card key={c.id} variant="outlined" sx={{ width: '100%' }}>
                  <CardContent
                    sx={{
                      pb: 1,
                      cursor: 'pointer',
                      '&:last-child': { pb: 1 },
                    }}
                    onClick={() => toggleExpand(c.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Business color="action" sx={{ flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {c.name}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                            <Chip size="small" label={`${t('companies.pricing_tier', { defaultValue: 'Tier' })} ${c.pricing_tier}`} />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {locationLabel(c)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t('companies.contact_count', { defaultValue: 'Contacts' })}: {c.contact_count}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <StatusPill
                              value={c.is_active}
                              trueLabel={t('companies.status_active', { defaultValue: 'Active' })}
                              falseLabel={t('companies.status_inactive', { defaultValue: 'Inactive' })}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <IconButton
                        aria-expanded={open}
                        aria-label={open ? t('common.collapse', { defaultValue: 'Collapse' }) : t('common.expand', { defaultValue: 'Expand' })}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(c.id);
                        }}
                        sx={mobileIconButtonSx}
                      >
                        {open ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2, pt: 0, pb: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="medium"
                      startIcon={<Edit />}
                      onClick={(e) => openEditCompany(e, c.id)}
                      sx={{ minHeight: 48 }}
                    >
                      {t('companies.edit', { defaultValue: 'Edit' })}
                    </Button>
                  </CardActions>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Divider />
                    <CardContent sx={{ pt: 2 }}>{expandedContent(c.id)}</CardContent>
                  </Collapse>
                </Card>
              );
            })}
            {companies.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                {t('companies.empty', { defaultValue: 'No companies found.' })}
              </Typography>
            )}
          </Box>
        ) : companies.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            {t('companies.empty', { defaultValue: 'No companies found.' })}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell>{t('companies.name', { defaultValue: 'Name' })}</TableCell>
                  <TableCell>{t('companies.pricing_tier', { defaultValue: 'Tier' })}</TableCell>
                  <TableCell>{t('companies.city', { defaultValue: 'City' })} / {t('companies.state', { defaultValue: 'State' })}</TableCell>
                  <TableCell align="right">{t('companies.contact_count', { defaultValue: 'Contacts' })}</TableCell>
                  <TableCell>{t('companies.active', { defaultValue: 'Status' })}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((c) => {
                  const open = expandedId === c.id;
                  return (
                    <React.Fragment key={c.id}>
                      <TableRow
                        hover
                        selected={open}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleExpand(c.id)}
                      >
                        <TableCell padding="checkbox">
                          <IconButton size="small" aria-label={open ? 'collapse' : 'expand'}>
                            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>{c.pricing_tier}</TableCell>
                        <TableCell>{locationLabel(c)}</TableCell>
                        <TableCell align="right">{c.contact_count}</TableCell>
                        <TableCell>
                          <StatusPill
                            value={c.is_active}
                            trueLabel={t('companies.status_active', { defaultValue: 'Active' })}
                            falseLabel={t('companies.status_inactive', { defaultValue: 'Inactive' })}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => openEditCompany(e, c.id)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: open ? undefined : 0 }} colSpan={7}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 1 }}>{expandedContent(c.id)}</Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={companyDialogOpen} onClose={() => setCompanyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCompanyId != null
            ? t('companies.edit', { defaultValue: 'Edit company' })
            : t('companies.new', { defaultValue: 'New Company' })}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.name', { defaultValue: 'Name' })}
            value={companyForm.name}
            onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.account_number', { defaultValue: 'Account number' })}
            value={companyForm.account_number}
            onChange={(e) => setCompanyForm({ ...companyForm, account_number: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            select
            label={t('companies.pricing_tier', { defaultValue: 'Pricing tier' })}
            value={companyForm.pricing_tier}
            onChange={(e) => setCompanyForm({ ...companyForm, pricing_tier: Number(e.target.value) })}
          >
            {PRICING_TIERS.map((tier) => (
              <MenuItem key={tier} value={tier}>
                {tier}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.address', { defaultValue: 'Address' })}
            value={companyForm.address}
            onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.city', { defaultValue: 'City' })}
            value={companyForm.city}
            onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.state', { defaultValue: 'State' })}
            value={companyForm.state}
            onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.zip_code', { defaultValue: 'ZIP code' })}
            value={companyForm.zip_code}
            onChange={(e) => setCompanyForm({ ...companyForm, zip_code: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.phone', { defaultValue: 'Phone' })}
            value={companyForm.phone}
            onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
          />
          {editingCompanyId != null && (
            <FormControlLabel
              control={
                <Switch
                  checked={companyForm.is_active}
                  onChange={(e) => setCompanyForm({ ...companyForm, is_active: e.target.checked })}
                />
              }
              label={t('companies.active', { defaultValue: 'Active' })}
              sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyDialogOpen(false)} sx={isMobile ? { minHeight: 48 } : undefined}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveCompany} sx={isMobile ? mobilePrimaryButtonSx : undefined}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact
            ? t('companies.edit_contact', { defaultValue: 'Edit contact' })
            : t('companies.add_contact', { defaultValue: 'Add contact' })}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.contact_first_name', { defaultValue: 'First name' })}
            value={contactForm.first_name}
            onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.contact_last_name', { defaultValue: 'Last name' })}
            value={contactForm.last_name}
            onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.contact_email', { defaultValue: 'Email' })}
            type="email"
            value={contactForm.email}
            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.contact_phone', { defaultValue: 'Phone' })}
            value={contactForm.phone}
            onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t('companies.contact_position', { defaultValue: 'Position' })}
            value={contactForm.position}
            onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
          />
          {editingContact && (
            <FormControlLabel
              control={
                <Switch
                  checked={contactForm.is_active}
                  onChange={(e) => setContactForm({ ...contactForm, is_active: e.target.checked })}
                />
              }
              label={t('companies.active', { defaultValue: 'Active' })}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)} sx={isMobile ? { minHeight: 48 } : undefined}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveContact} sx={isMobile ? mobilePrimaryButtonSx : undefined}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesPage;
